import { Box, Flex, Grid } from '@chakra-ui/react';
import { APIKongVX } from '@cyberkongz/api-interfaces';
import {
  APP_ROUTES_ABSOLUTE,
  AppLink,
  colors,
  EthereumIconWhite,
  KongActionsWrapper,
  PolygonIconWhite,
  pxToRem,
  SHARED_ROUTES,
} from '@cyberkongz/ui';
import { formatAddress, useKongVXEnhancedData } from '@cyberkongz/ui-blockchain';
import { getVXGradientBg } from '@cyberkongz/vx-viewer';
import { KongVXTheme, useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { useKongSubdomainInfo } from '@hooks/useSubdomainInfo';
import { ReactNode, useEffect, useMemo } from 'react';

import { ConnectedActions, NonConnectedActions, OwnerActions } from './KongActions';
import { KongInfoWrapper, KongTypeWithName } from './KongInfo';
import { KongTabs } from './KongTabs';
import { KongVXViewer } from './KongVXViewer';
import { KongPageWrapper, KongParticles, Label } from './Shared';

function componentToHex(c: number) {
  const hex = c.toString(16);
  return hex.length == 1 ? '0' + hex : hex;
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

function getKongVXTheme(hex: string): KongVXTheme {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // https://stackoverflow.com/a/3943023/112731
  const isDark = r * 0.299 + g * 0.587 + b * 0.114 > 186;

  return {
    isDark,
    text: isDark ? colors.textAlt.value : colors.text.value,
    textTab: isDark ? colors.textAlt.value : undefined,
    label: isDark ? colors.textAlt.value : colors.neutral[300].value,
  };
}

function KongInfo({ kong }: { kong: APIKongVX }) {
  const { kongVXTheme } = useSharedUI();
  const { owner, chain, isLocked } = useKongVXEnhancedData(kong);
  const { id, name, lvl } = kong;
  const subdomain = useKongSubdomainInfo({ kollectionType: 'vx', tokenId: id });
  const onL1 = chain === 'mainnet';

  return (
    <KongInfoWrapper
      {...{
        id,
        name,
        type: 'vx',
        openseaHref: SHARED_ROUTES.OPENSEA_KONG_VX(id, chain === 'mainnet'),
        blurHref: SHARED_ROUTES.BLUR_KONG_VX(id),
        css: { color: kongVXTheme?.text },
      }}
    >
      <Grid>
        <Label color={kongVXTheme?.label}>Owner</Label>
        <AppLink href={APP_ROUTES_ABSOLUTE.PROFILE_PUBLIC_ADDRESS(owner)}>
          {formatAddress(owner)}
        </AppLink>
      </Grid>
      {subdomain ? (
        <Grid>
          <Label color={kongVXTheme?.label}>Subdomain</Label>
          <AppLink href={`${SHARED_ROUTES.ENS}/name?ensName=${subdomain}`}>{subdomain}</AppLink>
        </Grid>
      ) : null}
      <Grid>
        <Label color={kongVXTheme?.label}>Level</Label>
        <Box>{lvl || 1}</Box>
      </Grid>
      <Grid>
        <Label color={kongVXTheme?.label}>Network</Label>
        <Flex css={{ alignItems: 'center', gap: 2, '& svg': { w: 5 } }}>
          {chain === 'mainnet' ? (
            <>
              <EthereumIconWhite />
              Ethereum
            </>
          ) : (
            <>
              <PolygonIconWhite />
              Polygon
            </>
          )}
        </Flex>
      </Grid>
      <Grid>
        <Label color={kongVXTheme?.label}>Status</Label>
        <Box>{isLocked ? 'Locked' : 'Unlocked'}</Box>
      </Grid>
    </KongInfoWrapper>
  );
}

function KongPod({ kong, ownerActions }: { kong: APIKongVX; ownerActions: ReactNode }) {
  const { kongVXTheme } = useSharedUI();
  const { id } = kong;

  return (
    <Flex
      css={{
        flexDirection: 'column',
        position: ['relative', 'sticky'],
        justifyContent: 'center',
        alignItems: 'flex-start',
        alignSelf: 'flex-start',
        pt: [10, null, 8],
        top: [0, pxToRem(100)],
        '&:before, &:after': {
          display: 'block',
          content: '""',
          position: 'absolute',
          // bottom: [pxToRem(200), pxToRem(50), 0],
          left: '50%',
          width: [pxToRem(1024), '100vw'],
        },
        ...(!kongVXTheme?.bgEffect
          ? {
              // '&:after': {
              //   w: '100vw',
              //   transform: ['translate(-50%, 115%)', 'translate(-50%, 110%)'],
              //   bg: `radial-gradient(50% 50% at 50% 50%, rgba(0,0,0, 0) 0%, rgba(0,0,0, 0.15) 100%)`,
              //   filter: 'blur(5px)',
              //   height: [pxToRem(760), '50vh'],
              //   minHeight: pxToRem(380),
              //   bottom: '50%',
              // },
            }
          : {
              '&:before': {
                bg: `url(/static/kong_page/effects/${kongVXTheme.bgEffect}) center/contain no-repeat`,
                transform: ['translate(-50%, 13%)', 'translate(-50%, -2%)'],
                pb: '142.62%',
              },
            }),
      }}
    >
      <Box
        css={{
          position: 'relative',
          display: 'inline-flex',
          zIndex: 3,
          width: '100%',
        }}
      >
        <Box
          css={{
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: ['translate(-50%, 30%)', 'translate(-50%, 25%)'],
            textAlign: 'center',
            fontFamily: 'heading',
            fontSize:
              id > 9999
                ? [pxToRem(115), pxToRem(140), pxToRem(200)]
                : id > 999
                ? [pxToRem(140), pxToRem(180), pxToRem(250)]
                : [pxToRem(200), pxToRem(240), pxToRem(300)],
            fontWeight: 'bold',
            letterSpacing: '-0.05em',
            lineHeight: 1,
            color: kongVXTheme?.text,
            whiteSpace: 'nowrap',
          }}
        >
          {id}
        </Box>
        <Box
          css={{
            height: [pxToRem(360), pxToRem(480), pxToRem(620)],
            w: '100%',
            position: 'relative',
            transform: ['none', 'translateY(-5%)'],
            zIndex: 2,
          }}
        >
          <KongVXViewer {...{ kong }} />
        </Box>
        <KongParticles css={{ w: '95%', h: '100%', transform: 'translate(-50%, -52.5%)' }} />
      </Box>
      {ownerActions ? (
        <KongActionsWrapper
          styles={{
            container: {
              bottom: 0,
              transform: `translate(-50%, 25%)`,
            },
          }}
        >
          {ownerActions}
        </KongActionsWrapper>
      ) : null}
    </Flex>
  );
}

const KONG_TYPE = 'vx';

export function KongVXPageView({
  isOwner = false,
  isConnected = false,
  kong,
}: {
  isOwner?: boolean;
  isConnected?: boolean;
  kong: APIKongVX;
}) {
  const { setKongVXTheme, kongVXTheme } = useSharedUI();
  const { gradient, cssColors } = useMemo(() => {
    const { gradient, color1, color2 } = getVXGradientBg(kong.id);
    const cssColors = getKongVXTheme(rgbToHex(color1));

    return { gradient, cssColors };
  }, [kong.id]);

  useEffect(() => {
    setKongVXTheme(cssColors);

    return () => {
      setKongVXTheme(undefined);
    };
  }, [cssColors]);

  return (
    <KongPageWrapper
      type={KONG_TYPE}
      css={{ bg: kongVXTheme?.bgEffect ? undefined : gradient }}
      infoComponent={<KongInfo {...{ kong }} />}
      podComponent={
        <KongPod
          {...{
            kong,
            ownerActions: isOwner ? (
              <OwnerActions kong={kong} type={KONG_TYPE} />
            ) : isConnected ? (
              <ConnectedActions type={KONG_TYPE} />
            ) : (
              <NonConnectedActions type={KONG_TYPE} />
            ),
          }}
        />
      }
      tabsComponent={
        <>
          <KongTypeWithName
            {...kong}
            type={KONG_TYPE}
            stylesWrapper={{ display: ['block', null, 'none'] }}
          />
          <KongTabs
            {...kong}
            infoComponent={<KongInfo {...{ kong }} />}
            type={KONG_TYPE}
            rawCssColors={cssColors}
          />
        </>
      }
    />
  );
}
